import { useRestoreFileVersion } from "@/lib/data/react-query/files/use-restore-file-version";
import type { FileVersion } from "@/lib/data/schemas/files";
import { isAxiosError } from "axios";
import {
  ActionConfirmationModal,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import {
  fileQueryOptions,
  fileVersionsQueryOptions,
} from "@/lib/data/react-query/files/canonical-files";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { useState } from "react";

export function useFileVersionState(fileId: string) {
  const file = useQuery(fileQueryOptions(fileId));
  const isOrdinaryFile =
    !!file.data &&
    !file.data.isFolder &&
    !file.data.skillId &&
    !file.data.systemFolder;
  const history = useInfiniteQuery(
    fileVersionsQueryOptions(
      fileId,
      isOrdinaryFile && !!file.data.currentVersionId,
    ),
  );
  const exposureUnavailable =
    isAxiosError(history.error) && history.error.response?.status === 404;
  const canViewHistory =
    isOrdinaryFile &&
    (history.isSuccess || (history.isError && !exposureUnavailable));
  const versions = history.data?.pages.flatMap((page) => page.data) ?? [];
  return {
    file: isOrdinaryFile ? file.data : undefined,
    history,
    versions,
    canViewHistory,
  };
}

export function FileVersionActions({
  fileId,
  state,
  selectedVersionId,
  onSelectVersion,
}: {
  fileId: string;
  state: ReturnType<typeof useFileVersionState>;
  selectedVersionId: string;
  onSelectVersion: (id: string) => void;
}) {
  const { file, history, versions } = state;
  const [restoreTarget, setRestoreTarget] = useState<FileVersion | null>(null);
  const restore = useRestoreFileVersion(fileId, file);
  const selected = versions.find((version) => version.id === selectedVersionId);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="group bg-light-primary text-primary hover:bg-light-primary/80 h-auto min-h-0 w-auto shrink-0 gap-1 rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold shadow-none"
            aria-label="Select file version"
          >
            Version {selected?.versionNumber ?? file?.currentVersionNumber}
            <SelectDropdownChevron />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="bottom"
          className="max-h-80 w-80 max-w-[calc(100vw-2rem)] overflow-y-auto"
        >
          <DropdownMenuRadioGroup
            value={selectedVersionId}
            onValueChange={onSelectVersion}
          >
            {versions.map((version) => (
              <div key={version.id} className="group flex items-stretch gap-1">
                <DropdownMenuRadioItem
                  value={version.id}
                  className="data-[state=checked]:bg-accent min-w-0 flex-[3] py-2"
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span>
                      Version {version.versionNumber}
                      {version.isCurrent ? " (current)" : ""}
                    </span>
                    <span className="text-muted-foreground truncate text-xs">
                      {version.uploadedByDisplayName || "Unknown"} ·{" "}
                      {new Date(version.createdAt).toLocaleDateString(
                        undefined,
                        {
                          dateStyle: "short",
                        },
                      )}
                    </span>
                  </span>
                </DropdownMenuRadioItem>
                {file?.scope !== "template" &&
                  file?.capabilities.canRestoreVersion &&
                  !version.isCurrent && (
                    <DropdownMenuItem
                      className="min-w-16 flex-1 justify-center self-stretch p-0 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100"
                      aria-label={`Revert to version ${version.versionNumber}`}
                      title={
                        version.sizeBytes > 5_000_000_000
                          ? "Versions larger than 5 GB cannot be restored."
                          : `Revert to version ${version.versionNumber}`
                      }
                      disabled={
                        restore.isPending || version.sizeBytes > 5_000_000_000
                      }
                      onSelect={() => {
                        setRestoreTarget(version);
                      }}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </DropdownMenuItem>
                  )}
              </div>
            ))}
          </DropdownMenuRadioGroup>
          {history.hasNextPage && (
            <DropdownMenuItem
              disabled={history.isFetchingNextPage}
              onSelect={(event) => {
                event.preventDefault();
                void history.fetchNextPage();
              }}
            >
              {history.isFetchingNextPage ? "Loading…" : "Load older versions"}
            </DropdownMenuItem>
          )}
          {history.isError && (
            <DropdownMenuItem onSelect={() => void history.refetch()}>
              Retry loading versions
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ActionConfirmationModal
        open={!!restoreTarget}
        setOpen={(next) => {
          if (!next) setRestoreTarget(null);
        }}
        title={`Revert to version ${restoreTarget?.versionNumber}?`}
        description="This saves the selected content as a new current version. All existing versions remain in history."
        buttonText="Revert"
        actionVariant="default"
        dismissOnOutsideClick
        isActionPending={restore.isPending}
        afterActionFn={async () => {
          if (!restoreTarget) return;
          try {
            await restore.mutateAsync(restoreTarget);
            onSelectVersion("");
          } catch {
            // The mutation shows the error and retains retry identity.
          }
        }}
      />
    </>
  );
}
